import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/app/account')({
  component: AccountPage,
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TierInfo = {
  key: string;
  displayName: string;
  propertyCap: number;
  source: 'stripe' | 'admin_comp';
  endsAt: string | null;
};

type MeUser = {
  id: string;
  email: string;
  displayName: string;
  preferredEmail: string;
  displayTimezone: string | null;
  invitesRemaining: number;
  currentTier: TierInfo | null;
};

type Session = {
  id: string;
  ipAddress: string;
  userAgent: string;
  createdAt: string;
  lastSeenAt: string;
  isCurrent: boolean;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseBrowser(ua: string): string {
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return 'Safari';
  return 'Browser';
}

function parsePlatform(ua: string): string {
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Macintosh|Mac OS X/.test(ua)) return 'Mac';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Device';
}

function formatUa(ua: string): string {
  return `${parseBrowser(ua)} on ${parsePlatform(ua)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function AccountPage() {
  const qc = useQueryClient();

  const { data: meData, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<{ user: MeUser }>('/v1/me'),
  });

  const { data: sessionsData, refetch: refetchSessions } = useQuery({
    queryKey: ['me', 'sessions'],
    queryFn: () => api.get<Session[]>('/v1/me/sessions'),
  });

  const user = meData?.user;
  const sessions = sessionsData ?? [];

  if (isLoading || !user) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="max-w-2xl mx-auto p-6 flex flex-col gap-8">
      <h1 className="text-xl font-semibold">Account</h1>
      <ProfileSection user={user} onSaved={() => void qc.invalidateQueries({ queryKey: ['me'] })} />
      <SecuritySection
        sessions={sessions}
        onPasswordChanged={() => void refetchSessions()}
        onSessionRevoked={() => void refetchSessions()}
        onRevokeAll={() => void refetchSessions()}
      />
      <SubscriptionSection tier={user.currentTier} invitesRemaining={user.invitesRemaining} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile section
// ---------------------------------------------------------------------------

function ProfileSection({ user, onSaved }: { user: MeUser; onSaved: () => void }) {
  const tzOptions = Intl.supportedValuesOf('timeZone');
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [displayName, setDisplayName] = useState(user.displayName);
  const [timezone, setTimezone] = useState(user.displayTimezone ?? localTz);

  useEffect(() => {
    setDisplayName(user.displayName);
    setTimezone(user.displayTimezone ?? localTz);
  }, [user, localTz]);

  const mutation = useMutation({
    mutationFn: () =>
      api.patch<{ user: MeUser }>('/v1/me', {
        displayName: displayName.trim(),
        displayTimezone: timezone,
      }),
    onSuccess: onSaved,
  });

  const dirty = displayName.trim() !== user.displayName || timezone !== (user.displayTimezone ?? localTz);

  return (
    <section className="rounded-lg border border-border p-5 flex flex-col gap-4">
      <h2 className="font-medium">Profile</h2>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium">Email</label>
        <p className="text-sm text-muted-foreground">{user.preferredEmail}</p>
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium" htmlFor="displayName">Display name</label>
        <input
          id="displayName"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium" htmlFor="timezone">Display timezone</label>
        <select
          id="timezone"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {tzOptions.map((tz) => (
            <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      {mutation.isError && (
        <p className="text-sm text-destructive">
          {mutation.error instanceof ApiError ? mutation.error.message : 'Save failed.'}
        </p>
      )}
      {mutation.isSuccess && <p className="text-sm text-green-600">Saved.</p>}

      <div>
        <Button
          size="sm"
          onClick={() => mutation.mutate()}
          disabled={!dirty || !displayName.trim() || mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Security section
// ---------------------------------------------------------------------------

function SecuritySection({
  sessions,
  onPasswordChanged,
  onSessionRevoked,
  onRevokeAll,
}: {
  sessions: Session[];
  onPasswordChanged: () => void;
  onSessionRevoked: () => void;
  onRevokeAll: () => void;
}) {
  return (
    <section className="rounded-lg border border-border p-5 flex flex-col gap-6">
      <h2 className="font-medium">Security</h2>
      <ChangePasswordForm onChanged={onPasswordChanged} />
      <hr className="border-border" />
      <SessionsList sessions={sessions} onRevoked={onSessionRevoked} onRevokeAll={onRevokeAll} />
    </section>
  );
}

function ChangePasswordForm({ onChanged }: { onChanged: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const mismatch = next.length > 0 && confirm.length > 0 && next !== confirm;

  const mutation = useMutation({
    mutationFn: () =>
      api.post<void>('/v1/me/change-password', { currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
      onChanged();
    },
  });

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-medium">Change password</h3>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground" htmlFor="cur-pw">Current password</label>
        <input
          id="cur-pw"
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          autoComplete="current-password"
          className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground" htmlFor="new-pw">New password</label>
        <input
          id="new-pw"
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          autoComplete="new-password"
          className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground" htmlFor="confirm-pw">Confirm new password</label>
        <input
          id="confirm-pw"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          className={`rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${mismatch ? 'border-destructive' : 'border-input'}`}
        />
        {mismatch && <p className="text-xs text-destructive">Passwords do not match.</p>}
      </div>

      {mutation.isError && (
        <p className="text-sm text-destructive">
          {mutation.error instanceof ApiError ? mutation.error.message : 'Password change failed.'}
        </p>
      )}
      {mutation.isSuccess && <p className="text-sm text-green-600">Password updated. Other sessions have been signed out.</p>}

      <div>
        <Button
          size="sm"
          onClick={() => mutation.mutate()}
          disabled={!current || !next || !confirm || mismatch || next.length < 8 || mutation.isPending}
        >
          {mutation.isPending ? 'Updating…' : 'Update password'}
        </Button>
      </div>
    </div>
  );
}

function SessionsList({
  sessions,
  onRevoked,
  onRevokeAll,
}: {
  sessions: Session[];
  onRevoked: () => void;
  onRevokeAll: () => void;
}) {
  const revokeMutation = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/v1/me/sessions/${id}`),
    onSuccess: onRevoked,
  });

  const revokeAllMutation = useMutation({
    mutationFn: async () => {
      const others = sessions.filter((s) => !s.isCurrent);
      await Promise.all(others.map((s) => api.delete<void>(`/v1/me/sessions/${s.id}`)));
    },
    onSuccess: onRevokeAll,
  });

  const otherCount = sessions.filter((s) => !s.isCurrent).length;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Active sessions</h3>
        {otherCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => revokeAllMutation.mutate()}
            disabled={revokeAllMutation.isPending}
          >
            {revokeAllMutation.isPending ? 'Signing out…' : `Sign out ${otherCount} other ${otherCount === 1 ? 'session' : 'sessions'}`}
          </Button>
        )}
      </div>

      {sessions.length === 0 && (
        <p className="text-sm text-muted-foreground">No active sessions found.</p>
      )}

      <div className="flex flex-col gap-2">
        {sessions.map((s) => (
          <div
            key={s.id}
            className="flex items-start justify-between gap-4 rounded-md border border-border px-4 py-3"
          >
            <div className="flex flex-col gap-0.5 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate">{formatUa(s.userAgent)}</span>
                {s.isCurrent && (
                  <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded font-medium shrink-0">
                    This device
                  </span>
                )}
              </div>
              <span className="text-xs text-muted-foreground">{s.ipAddress}</span>
              <span className="text-xs text-muted-foreground">
                Last seen {formatDate(s.lastSeenAt)}
              </span>
            </div>
            {!s.isCurrent && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => revokeMutation.mutate(s.id)}
                disabled={revokeMutation.isPending}
              >
                Revoke
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subscription section
// ---------------------------------------------------------------------------

function SubscriptionSection({
  tier,
  invitesRemaining,
}: {
  tier: TierInfo | null;
  invitesRemaining: number;
}) {
  return (
    <section className="rounded-lg border border-border p-5 flex flex-col gap-4">
      <h2 className="font-medium">Subscription</h2>

      {tier ? (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{tier.displayName}</span>
            <span className="text-xs text-muted-foreground capitalize">
              ({tier.source === 'admin_comp' ? 'Admin grant' : 'Stripe'})
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            Up to {tier.propertyCap} {tier.propertyCap === 1 ? 'property' : 'properties'}
          </p>
          {tier.endsAt && (
            <p className="text-sm text-amber-600">
              Expires {formatDate(tier.endsAt)}
            </p>
          )}
          {!tier.endsAt && (
            <p className="text-sm text-muted-foreground">No expiry</p>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No active subscription. Contact your administrator to have a tier assigned.
        </p>
      )}

      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium">Invites remaining</span>
        <span className="text-sm text-muted-foreground">{invitesRemaining}</span>
      </div>
    </section>
  );
}
